import React, {useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useHistory, useLocation} from "react-router-dom"
import {
    TextField, Divider, Paper, Button, Typography, useTheme, Snackbar, Alert
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import InfoIcon from "@mui/icons-material/Info"
import {isLoginRequired} from "../../helpers/authHelper"
import {authenticationActions, planApiActions} from "../../actions"
import {ActionButtons} from "./ActionButtons"
import {ConfirmDialog} from "../ConfirmDialog"

const SnackBar = ({snackbar, handleSnackBarClose}) => {
    return (
        <Snackbar
            open={snackbar.length > 0}
            autoHideDuration={3000}
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            onClose={handleSnackBarClose}
        >
            <Alert severity="info" sx={{width: '100%'}}>
                {snackbar}
            </Alert>
        </Snackbar>
    )
}

const TitleWithDeleteButton = ({plan, project}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
    const isDeleteEnabled = !project

    return (
        <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
        >
            <Grid>
                <Typography component="h1" variant="h5">
                    Plan {plan.id}
                </Typography>
            </Grid>
            {isDeleteEnabled && (<Grid mb={theme.spacing(1)}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setConfirmationDialogOpen(true)
                        }}
                        style={{
                            color: "black", borderColor: "black"
                        }}
                    >
                        Löschen
                    </Button>
                    <div>
                        <ConfirmDialog
                            title="Plan löschen"
                            open={confirmationDialogOpen}
                            setOpen={setConfirmationDialogOpen}
                            onConfirm={() => {
                                dispatch(authenticationActions.extendLoginIfRequired())
                                dispatch(planApiActions.deletePlan(plan.id))
                            }}
                        >
                            <Typography component="p" variant="body2">
                                Sind Sie sicher dass Sie diesen Plan löschen wollen?
                            </Typography>
                        </ConfirmDialog>
                    </div>
                </Grid>

            )}
        </Grid>
    )
}

const CopyInfo = ({}) => {
    const theme = useTheme()

    return (
        <Grid container mt={theme.spacing(2)} direction="row">
            <Grid size={2}>
                <InfoIcon style={{marginRight: "1em"}}/>
            </Grid>
            <Grid size={10}>
                <Typography
                    component="p"
                    variant="body2"
                    style={{whiteSpace: 'pre-line'}}
                >
                    Auf Artikelnummer oder Bezeichnung klicken um den Text in die Zwischenablage zu speichern!
                </Typography>
            </Grid>
        </Grid>
    )
}

const VaultElement = ({element, setSnackbar}) => {
    const theme = useTheme()
    const quantityFactor = Number.isFinite(element.subFloorLidCoverCount) && element.subFloorLidCoverCount > 0
        ? element.subFloorLidCoverCount
        : 1;
    const quantity = element.quantity * quantityFactor;

    return (
        <Grid container mt={theme.spacing(2)} direction="row">
            <Grid size={2}>
                <Typography component="p" variant="body2">
                    <b>{quantity} Stk</b>
                </Typography>
            </Grid>
            <Grid size={10}>
                <Typography
                    component="p"
                    variant="body2"
                    sx={{cursor: "pointer"}}
                    onClick={_ => {
                        navigator.clipboard.writeText(element.partNumber)
                        setSnackbar("Artikelnummer in Zwischenablage kopiert ")
                    }}
                >
                    <b>{element.partNumber}</b>
                </Typography>
                <Typography
                    component="p"
                    variant="body2"
                    style={{whiteSpace: 'pre-line'}}
                    sx={{cursor: "pointer"}}
                    onClick={_ => {
                        navigator.clipboard.writeText(element.description)
                        setSnackbar("Artikelbeschreibung in Zwischenablage kopiert")
                    }}
                >
                    {element.description}
                </Typography>
            </Grid>
        </Grid>
    )
}

const Creator = ({creator, plan}) => {
    const theme = useTheme()

    return (
        <Grid container mt={theme.spacing(3)} direction="row">
            <Grid size={5}>
                <Typography component="p" variant="body2">
                    Plan erstellt
                </Typography>
            </Grid>
            <Grid size={7}>
                <Typography component="p" variant="body2">
                    <b>
                        {creator.firstname} {creator.lastname}
                    </b>
                </Typography>
                <Typography component="p" variant="body2">
                    {creator.email}
                </Typography>
                <Typography component="p" variant="body2">
                    {new Date(plan.created).toLocaleString()}
                </Typography>
            </Grid>
        </Grid>)
}

const Owner = ({customer, plan}) => {
    const theme = useTheme()
    const [showOwnerInput, setShowOwnerInput] = useState(false)
    const [owner, setOwner] = useState("")
    const location = useLocation()
    const dispatch = useDispatch()
    const history = useHistory()
    const authentication = useSelector(state => state.authentication)

    const setOwnerButtonOnClick = () => {
        if (isLoginRequired(authentication)) {
            history.push("/login?navigateTo=" + location.pathname)
            return
        }
        dispatch(authenticationActions.extendLoginIfRequired())
        dispatch(planApiActions.setOwner(plan.id, owner))
    }

    return (
        <Grid
            container
            mt={theme.spacing(1)}
            direction="row"
            alignItems="flex-start"
        >
            <Grid size={5}>
                <Typography component="p" variant="body2">
                    Eigentümer
                </Typography>
            </Grid>
            <Grid size={7}>
                <Typography component="p" variant="body2">
                    <b>{customer.email}</b>
                </Typography>
                <Grid mt={theme.spacing(1)}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setShowOwnerInput(true)
                        }}
                    >
                        Ändern
                    </Button>
                </Grid>

                {showOwnerInput && (<Grid mt={theme.spacing(2)}>
                    <TextField
                        sx={{
                            '& .MuiInputBase-input': {
                                height: 22, marginLeft: 0, marginRight: '5px', fontSize: 13,
                            },
                        }}
                        size={"small"}
                        slotProps={{inputLabel: {shrink: true}}}
                        margin="dense"
                        variant="outlined"
                        placeholder={"Neue Eigentümer E-Mail"}
                        value={owner}
                        onChange={e => {
                            setOwner(e.target.value)
                        }}
                    />
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setOwnerButtonOnClick()}
                        disabled={owner.length < 1 || owner === plan.owner}
                    >
                        Speichern
                    </Button>
                </Grid>)}
            </Grid>
        </Grid>)
}

const Description = ({plan, setSnackbar}) => {
    const theme = useTheme()
    return (
        <Grid container mt={theme.spacing(1)} direction="row">
            <Grid size={5}>
                <Typography component="p" variant="body2">
                    Schachtbezeichnung
                </Typography>
            </Grid>
            <Grid size={7}>
                <Typography
                    display="block"
                    component="p"
                    variant="body2"
                    sx={{cursor: "pointer"}}
                    onClick={_ => {
                        navigator.clipboard.writeText(plan.description)
                        setSnackbar("Schachtbezeichnung in Zwischenablage kopiert")
                    }}
                >
                    {plan.description}
                </Typography>
            </Grid>
        </Grid>
    )
}

const Actions = ({plan, project}) => {
    const theme = useTheme()

    return (<Grid
        marginTop={theme.spacing(2)}
        display="flex"
        justifyContent="center"
    >
        <ActionButtons plan={plan} project={project}></ActionButtons>
    </Grid>)
}

export const PlanPanel = props => {
    const {plan, project, creator, customer, vaultElements} = props
    const [snackbar, setSnackbar] = React.useState("");
    const theme = useTheme()
    const isAdmin = useSelector(state => state.user.isAdmin)

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar("");
    };

    return (<Paper square={true} sx={{
        background: "white", padding: theme.spacing(4)
    }}>
        <SnackBar snackbar={snackbar} handleSnackBarClose={handleSnackBarClose}/>
        <TitleWithDeleteButton plan={plan} project={project}/>
        <Divider style={{background: "black"}}/>
        <Grid mt={theme.spacing(4)}></Grid>
        <CopyInfo/>
        {vaultElements.map(element => <VaultElement key={element.partNumber} element={element}
                                                    setSnackbar={setSnackbar}/>)}
        <Creator creator={creator} plan={plan}/>
        {isAdmin && Owner({customer, plan})}
        <Description plan={plan} setSnackbar={setSnackbar}/>
        <Actions plan={plan} project={project}/>

    </Paper>)
}
